import { createSelector } from 'reselect';
import get from 'lodash/get';
import some from 'lodash/some';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import { validateAppointment } from '../../utils/dashboard/validateAppointmentByFilter';
import { makeSelectorFactory } from '../../utils/redux/selectAdapter';

export const selectDashboardState = state => get(state, 'dashboard');

const getSelectors = makeSelectorFactory(selectDashboardState);

/**
 *  APPOINTMENT LIST
 * */
export const selectAppointmentListState = createSelector(
  selectDashboardState,
  state => get(state, 'appointmentList'),
);

export const selectAppointmentList = createSelector(
  selectAppointmentListState,
  state => get(state, 'list', []),
);

export const selectAppointmentListLoading = createSelector(
  selectAppointmentListState,
  state => get(state, 'sending', false),
);

export const selectActiveAppointment = createSelector(
  selectAppointmentListState,
  state => get(state, 'activeAppointment', undefined),
);

/**
 *  APPOINTMENT DETAILS
 * */
export const selectCurrentAppointmentDetailsState = createSelector(
  selectDashboardState,
  state => get(state, 'appointmentDetailsReq'),
);

export const selectCurrentAppointmentDetails = createSelector(
  selectCurrentAppointmentDetailsState,
  state => get(state, 'appointment', {}),
);

export const selectCurrentAppointmentId = createSelector(
  selectCurrentAppointmentDetailsState,
  state => get(state, 'appointmentId', null),
);

export const {
  selectStateError: selectAppointmentDetailsError,
  selectStatePending: selectAppointmentDetailsPending,
} = getSelectors('appointmentDetailsReq');

/**
 *  FILTER
 * */
export const selectDashboardFilterState = createSelector(
  selectDashboardState,
  state => get(state, 'filter'),
);

export const selectDashboardNotEmptyFilterState = createSelector(
  selectDashboardFilterState,
  options => omitBy(options, isEmpty),
);

export const selectDashboardDateFilter = createSelector(
  selectDashboardFilterState,
  state => get(state, 'date'),
);

export const selectDashboardFilterLoadStatus = createSelector(
  selectDashboardFilterState,
  state => get(state, 'filterLoaded'),
);

export const selectDashboardAppliedFilterCounter = createSelector(
  selectDashboardFilterState,
  ({ locations, services, statuses }) =>
    [locations, services, statuses].reduce((acc, list) => acc + list.length, 0),
);

/**
 *  FILTER OPTIONS
 * */
export const selectDashboardFilterOptionsState = createSelector(
  selectDashboardState,
  state => get(state, 'locations'),
);

export const selectDashboardFilterLocationsKeys = createSelector(
  selectDashboardFilterOptionsState,
  state => get(state, 'result.locations', []),
);

export const makeSelectDashboardFilterOptsEntity = entity =>
  createSelector(
    selectDashboardFilterOptionsState,
    state => get(state, ['entities', entity], {}),
  );

export const selectDashboardFilterLocations = createSelector(
  selectDashboardFilterLocationsKeys,
  makeSelectDashboardFilterOptsEntity('locations'),
  (keys, entities) => keys.map(key => entities[key]),
);

export const selectDashboardFilterServices = createSelector(
  makeSelectDashboardFilterOptsEntity('services'),
  entities => Object.keys(entities).map(key => entities[key]),
);

export const selectDashboardFilterStatuses = createSelector(
  makeSelectDashboardFilterOptsEntity('statuses'),
  entities => Object.keys(entities).map(key => entities[key]),
);

export const selectStatusesByServiceId = id =>
  createSelector(
    makeSelectDashboardFilterOptsEntity('services'),
    makeSelectDashboardFilterOptsEntity('statuses'),
    (services, statuses) => {
      const service = get(services, id);

      if (service) {
        return service.statuses.map(statusId => statuses[statusId]);
      }
      return [];
    },
  );

// Locations without filter

export const selectDashboardLocationsList = createSelector(
  selectDashboardFilterOptionsState,
  state => get(state, 'locationsList'),
);

export const selectStatusesByLocationAndService = (locationId, serviceId) =>
  createSelector(
    selectDashboardLocationsList,
    list => {
      if (isEmpty(list)) return [];
      const currentLocation = list.find(location => location.id === locationId);
      const service = get(currentLocation, 'services', []).find(
        item => item.id === serviceId,
      );
      return get(service, 'statuses', []);
    },
  );

/**
 *  CHANGE EDIT AMOUNT MODAL
 * */

export const selectDashboardEditPaymentAmountState = createSelector(
  selectDashboardState,
  state => get(state, 'editPaymentAmountModal'),
);

export const selectDashboardEditAmountOpened = createSelector(
  selectDashboardEditPaymentAmountState,
  state => get(state, 'opened', false),
);

export const selectDashboardEditAmountAppointment = createSelector(
  selectDashboardEditPaymentAmountState,
  state => get(state, 'appointment', null),
);

/**
 *  SET PAID STATUS MODAL
 * */

export const selectDashboardChangePaidStatusState = createSelector(
  selectDashboardState,
  state => get(state, 'changePaidStatusModal'),
);

export const selectDashboardChangePaidStatusOpened = createSelector(
  selectDashboardChangePaidStatusState,
  state => get(state, 'opened', false),
);

export const selectDashboardChangePaidStatusAppointment = createSelector(
  selectDashboardChangePaidStatusState,
  state => get(state, 'appointment', null),
);

/**
 *  CHANGE ROOM MODAL
 * */
export const selectDashboardChangeRoomState = createSelector(
  selectDashboardState,
  state => get(state, 'changeRoomModal'),
);

export const selectDashboardChangeRoomOpened = createSelector(
  selectDashboardChangeRoomState,
  state => get(state, 'opened', false),
);

export const selectDashboardChangeRoomAppointment = createSelector(
  selectDashboardChangeRoomState,
  state => get(state, 'appointment', ''),
);

export const selectDashboardChangeRoomLoading = createSelector(
  selectDashboardState,
  state => get(state, 'updateRoom.sending', false),
);

const selectChangeAppointmentStatusState = createSelector(
  selectDashboardState,
  state => get(state, 'changeStatus'),
);
const selectPinAppointmentPinState = createSelector(
  selectDashboardState,
  state => get(state, 'pinAppointment'),
);

export const selectUpdateAppointmentLoading = createSelector(
  selectChangeAppointmentStatusState,
  selectPinAppointmentPinState,
  (...states) => some(states, ({ sending }) => sending),
);

export const {
  selectStateError: selectChangeStatusError,
  selectStatePending: selectChangeStatusPending,
} = getSelectors('changeStatus');

/**
 *  FILTERED APPOINTMENT LIST
 * */
export const selectFilteredAppointmentList = createSelector(
  selectAppointmentList,
  selectDashboardNotEmptyFilterState,
  (list, filterData) => {
    return list.filter(appointment =>
      validateAppointment(appointment, filterData),
    );
  },
);

// Add record to the DASHBOARD

export const selectDashboardRecord = createSelector(
  selectDashboardState,
  state => get(state, 'record'),
);

export const selectDashboardPatientRecordValidationError = createSelector(
  selectDashboardRecord,
  state => get(state, 'patientValidationError'),
);

export const selectDashboardPatientCommonErrors = createSelector(
  selectDashboardRecord,
  state => get(state, 'commonErrors'),
);

export const selectDashboardPatientRecord = createSelector(
  selectDashboardRecord,
  state => get(state, 'patient'),
);

export const selectAppointmentsLocationsState = createSelector(
  selectDashboardState,
  state => get(state, 'appointmentLocations'),
);

export const selectAppointmentsRecordLocations = createSelector(
  selectAppointmentsLocationsState,
  state => get(state, 'locations', []),
);

export const {
  selectStatePending: selectLoadingAppointmentsLocations,
} = getSelectors('appointmentLocations');

export const {
  selectStatePending: selectSendingCreateAppointmentReq,
  selectStateRejected: selectRejectedCreateAppointmentReq,
  selectStateError: selectCreateAppointmentReqError,
} = getSelectors('createAppointmentReq');

export const {
  selectStatePending: selectSendingValidatePatientInfo,
} = getSelectors('validatePatientInfo');



// WEBPACK FOOTER //
// ./src/redux/dashboard/selectors.js